import * as z from 'zod';

import { invoiceAccountReceivablePaymentQuerySchema } from '@/_pages/invoices/shared';
import { invoiceQueryLinkerExtendedSchema } from '@/_pages/invoices/shared/schemas/invoices/invoice-query-schema-extended';
import { companyQueryLinkerSchema } from '@/_schemas/companies/company-query-linker-schema';
import { fileQuerySchema } from '@/_schemas/file';
import { currencyInput, paginatedResponseSchema } from '@/_schemas/generic';

import { phoneNumberQuerySchema } from '../phone-numbers';

export const accountsReceivablesQuerySchema = z.object({
  id: z.number(),
  invoice_id: z.number(),
  invoice: invoiceQueryLinkerExtendedSchema.nullable(),
  amount_original: currencyInput.querySchema(),
  balance_due: currencyInput.querySchema(),
  total_paid: currencyInput.querySchema(),
  current_due: currencyInput.querySchema(),
  account_receivable_payments: z.array(invoiceAccountReceivablePaymentQuerySchema),
});

export type AccountsReceivablesQuerySchema = z.infer<typeof accountsReceivablesQuerySchema>;

export const accountsReceivablesAllQuerySchema = paginatedResponseSchema(accountsReceivablesQuerySchema);

export type AccountsReceivablesAllQuerySchema = z.infer<typeof accountsReceivablesAllQuerySchema>;

const accountReceivableClientAggregateTotalsSchema = z.object({
  total_invoiced: currencyInput.querySchema(),
  total_paid: currencyInput.querySchema(),
  total_balance_due: currencyInput.querySchema(),
  total_account_credit: currencyInput.querySchema(),
  balance_1_30: currencyInput.querySchema(),
  balance_31_60: currencyInput.querySchema(),
  balance_61_90: currencyInput.querySchema(),
  balance_91_plus: currencyInput.querySchema(),
  current_due: currencyInput.querySchema(),
});

const accountReceivableClientAggregateOverAllTotalsSchema = z.object({
  total_invoiced: currencyInput.querySchema(),
  total_paid: currencyInput.querySchema(),
  total_balance_due: currencyInput.querySchema(),
  total_account_credit: currencyInput.querySchema(),
  total_balance_1_30: currencyInput.querySchema(),
  total_balance_31_60: currencyInput.querySchema(),
  total_balance_61_90: currencyInput.querySchema(),
  total_balance_91_plus: currencyInput.querySchema(),
  total_current_due: currencyInput.querySchema(),
  total_aggregated_values: currencyInput.querySchema(),
});

const accountReceivableClientAggregateBrandSchema = z.object({
  id: z.number(),
  name: z.string(),
  icon: fileQuerySchema.nullish(),
});

const accountReceivableClientAggregateClientSchema = companyQueryLinkerSchema.extend({
  brand: accountReceivableClientAggregateBrandSchema.nullish(),
  phone_numbers: z.array(phoneNumberQuerySchema).nullish(),
  upcoming_due_date: z.string().nullable(),
  subsidiaries: z.array(companyQueryLinkerSchema).nullish(),
});

export type AccountReceivableClientAggregateClientSchema = z.infer<typeof accountReceivableClientAggregateClientSchema>;

export const accountReceivableClientAggregateSchema = z.object({
  client: accountReceivableClientAggregateClientSchema,
  totals: accountReceivableClientAggregateTotalsSchema,
  subsidiaries: z.array(
    z.object({
      client: accountReceivableClientAggregateClientSchema,
    })
  ),
});

export type AccountReceivableClientAggregateSchema = z.infer<typeof accountReceivableClientAggregateSchema>;

export const accountReceivableClientAggregatesQuerySchema = z.object({
  overall_totals: accountReceivableClientAggregateOverAllTotalsSchema,
  clients: z.array(accountReceivableClientAggregateSchema),
  links: z.object({
    first: z.string(),
    last: z.string(),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  meta: z.object({
    current_page: z.number().prefault(1),
    from: z.number(),
    last_page: z.number(),
    links: z.array(z.object({ url: z.string().nullable(), label: z.string(), active: z.boolean() })),
    path: z.string(),
    per_page: z.number(),
    to: z.number(),
    total: z.number(),
  }),
});

export type AccountReceivableClientAggregatesQuerySchema = z.infer<typeof accountReceivableClientAggregatesQuerySchema>;
